<script>
// Khối thông tin khách ở cột phải khung chat — bản BonBoZ thay cho
// ContactInfo.vue của Chatwoot (file gốc giữ nguyên, chỉ đổi một dòng import
// trong ContactPanel.vue để merge bản Chatwoot mới không bị đụng).
//
// Khác bản gốc: bỏ email / công ty / vị trí vì kênh Facebook không có; bỏ các
// nút gộp, xoá, sửa danh bạ vì đó là việc quản trị; thêm các trường hồ sơ khách.
//
// Tên trường nằm ngay trong ô nhập và biến mất khi đã điền. Cột phải chỉ rộng
// 300px, dành hẳn một cột nhãn là mất gần một phần ba bề ngang, trong khi nhìn
// giá trị hầu hết là biết ngay trường gì.
//
// Ô nhập là khối bo góc nền xám nhạt chứ không phải gạch chân: ô trống nhìn ra
// ngay là chỗ điền được, mà không cần vẽ thêm viền quanh từng ô.
//
// Lưu ý khi nâng cấp Chatwoot: bản gốc đổi thì file này không tự nhận được,
// phải xem lại diff của ContactInfo.vue một lần.
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import {
  chuanHoaSdt,
  hienThiSdt,
  sangE164,
  timSdtTrongText,
} from 'dashboard/helper/phoneNumber';
import Avatar from 'next/avatar/Avatar.vue';
import SocialIcons from './SocialIcons.vue';
import VoiceCallButton from 'dashboard/components-next/Contacts/VoiceCallButton.vue';
import InlineInput from 'dashboard/components-next/inline-input/InlineInput.vue';
import { TRUONG_NHAP, giaTri, tuoiHopLe } from 'dashboard/helper/hoSoKhach';

// Bề rộng từng ô trong hàng chảy. Giới tính và tuổi cố định vừa đủ chữ để còn
// chỗ cho số điện thoại đứng cùng hàng; hai ô còn lại chia đôi hàng dưới.
const BE_RONG = {
  kh_gioi_tinh: 'w-[86px]',
  kh_tuoi: 'w-[54px]',
  kh_dia_chi: 'w-full',
  kh_loai_khach: 'flex-1 basis-[calc(50%-0.5rem)]',
  kh_trang_thai: 'flex-1 basis-[calc(50%-0.5rem)]',
};

export default {
  components: {
    Avatar,
    SocialIcons,
    VoiceCallButton,
    InlineInput,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    showAvatar: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isEditingName: false,
      editName: '',
      // Giá trị đang gõ trong ô SĐT. Tách khỏi contact để so được "đã đổi hay
      // chưa" mà bật/tắt nút Lưu.
      sdtNhap: '',
      // Danh sách số bắt được trong chat, thả xuống ngay khi bấm vào ô SĐT.
      moGoiY: false,
      truongNhap: TRUONG_NHAP,
      BE_RONG,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
    }),
    contactProfileLink() {
      return `/app/accounts/${this.$route.params.accountId}/contacts/${this.contact.id}`;
    },
    additionalAttributes() {
      return this.contact.additional_attributes || {};
    },
    // Chỉ quét tin khách gửi: tin nhân viên cũng trích dẫn số, quét cả hai thì
    // gợi ý lại đúng số vừa nhắn. Tin mới nhất lên đầu.
    sdtTuChat() {
      const tuTinKhach = (this.currentChat?.messages || [])
        .filter(tin => tin.message_type === MESSAGE_TYPE.INCOMING)
        .reverse()
        .flatMap(tin => timSdtTrongText(tin.content || ''));
      return [...new Set(tuTinKhach)];
    },
    // Số đầu tiên đã được máy chủ tự điền lúc tin nhắn tới, ở đây chỉ gợi ý
    // thêm. Không bao giờ tự đè lên số đang có — đó là việc của người trực.
    sdtGoiY() {
      const daLuu = chuanHoaSdt(this.contact.phone_number || '');
      return this.sdtTuChat.filter(sdt => sdt !== daLuu).slice(0, 3);
    },
    // So sánh sau chuẩn hoá: gõ `0912...` với `+84912...` là cùng một số, nút
    // Lưu không được sáng lên vô cớ.
    sdtDaDoi() {
      return (
        chuanHoaSdt(this.sdtNhap) !== chuanHoaSdt(this.contact.phone_number)
      );
    },
    socialProfiles() {
      const {
        social_profiles: socialProfiles,
        screen_name: twitterScreenName,
        social_telegram_user_name: telegramUsername,
      } = this.additionalAttributes;

      const telegram = socialProfiles?.telegram || telegramUsername || '';
      const twitter = socialProfiles?.twitter || twitterScreenName || '';

      return {
        ...(socialProfiles || {}),
        twitter,
        telegram,
      };
    },
  },
  watch: {
    'contact.id': {
      handler(id) {
        this.$store.dispatch('contacts/fetchContactableInbox', id);
      },
      immediate: true,
    },
    'contact.phone_number': {
      handler(so) {
        this.sdtNhap = hienThiSdt(so);
      },
      immediate: true,
    },
  },
  methods: {
    dynamicTime,
    giaTri,
    // Giá trị cũ không còn trong danh sách chọn vẫn phải hiện ra, không thì
    // nhìn như ô trống và người trực đè lên mà không biết vừa xoá mất gì.
    mucChon(truong) {
      const dangCo = giaTri(this.contact, truong.khoa);
      return truong.chon.includes(dangCo)
        ? truong.chon
        : [...truong.chon, dangCo];
    },
    // Gửi cả cụm thuộc tính hiện có rồi mới ghi đè một khoá: hai người trực sửa
    // hai trường cùng lúc thì bản gửi sau không được xoá mất bản gửi trước.
    luuTruong(truong, value) {
      const moi = truong.so ? tuoiHopLe(value) : value;
      if (moi === giaTri(this.contact, truong.khoa)) return;
      this.updateContactField({
        customAttributes: {
          ...(this.contact.custom_attributes || {}),
          [truong.khoa]: moi,
        },
      });
    },
    chonGoiY(sdt) {
      // Chỉ điền vào ô chứ không lưu ngay: bấm nhầm một dòng trong danh sách
      // không được phép đè lên số đã xác nhận qua điện thoại.
      this.sdtNhap = sdt;
      this.moGoiY = false;
      this.$refs.oSdt?.focus();
    },
    startEditingName() {
      this.editName = this.contact.name || '';
      this.isEditingName = true;
      this.$nextTick(() => {
        this.$refs.nameInput?.focus();
      });
    },
    saveNameEdit() {
      if (!this.isEditingName) return;
      this.isEditingName = false;
      const trimmed = this.editName.trim();
      if (trimmed && trimmed !== this.contact.name) {
        this.updateContactField({ name: trimmed });
      }
    },
    cancelNameEdit() {
      this.isEditingName = false;
    },
    // Chuẩn hoá trước khi lưu để mỗi người một kiểu gõ vẫn ra cùng một số; số
    // sai thì báo lại chứ không ghi đè số đang có. Nhân viên sửa và xoá được
    // như quản trị, mọi thay đổi đều vào nhật ký nên truy ngược được.
    luuSdt(raw) {
      const value = (raw ?? '').trim();
      const sdt = value ? chuanHoaSdt(value) : '';
      if (value && !sdt) {
        useAlert(this.$t('BONBOZ.CONTACT.PHONE_INVALID'));
        return;
      }
      // Số đang lưu và số vừa gõ khác định dạng, phải chuẩn hoá rồi mới so,
      // không thì lần nào cũng tưởng là số mới.
      if (sdt === chuanHoaSdt(this.contact.phone_number || '')) return;
      this.onFieldUpdate('phone_number', sdt ? sangE164(sdt) : '');
    },
    onFieldUpdate(field, value) {
      this.updateContactField({ [field]: value });
    },
    async updateContactField(attrs) {
      const contactId = this.contact.id;
      try {
        await this.$store.dispatch('contacts/update', {
          id: contactId,
          ...attrs,
        });
        useAlert(this.$t('CONTACT_FORM.SUCCESS_MESSAGE'));
        await this.$store.dispatch('contacts/fetchContactableInbox', contactId);
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const detail = error.contactErrorDetail;
          if (detail) {
            useAlert(detail);
          } else {
            const invalidAttrs = Array.isArray(error.data) ? error.data : [];
            if (invalidAttrs.includes('email')) {
              useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
            } else if (invalidAttrs.includes('phone_number')) {
              useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
            } else {
              useAlert(this.$t('CONTACT_FORM.ERROR_MESSAGE'));
            }
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(error.message || this.$t('CONTACT_FORM.ERROR_MESSAGE'));
        }
      }
    },
  },
};
</script>

<template>
  <div class="relative items-center w-full p-3">
    <div class="flex flex-col w-full gap-2.5 text-left rtl:text-right">
      <!-- Một hàng duy nhất: ảnh đại diện nhỏ, tên, hai biểu tượng. Ảnh to
           không nói thêm điều gì mà chiếm mất một phần ba khối. -->
      <div v-if="showAvatar" class="flex items-center w-full min-w-0 gap-2">
        <Avatar
          :src="contact.thumbnail"
          :name="contact.name"
          :status="contact.availability_status"
          :size="24"
          hide-offline-status
        />
        <InlineInput
          v-if="isEditingName"
          ref="nameInput"
          v-model="editName"
          custom-input-class="!text-sm !font-medium"
          class="!w-fit"
          @enter-press="saveNameEdit"
          @escape-press="cancelNameEdit"
          @blur="saveNameEdit"
        />
        <h3
          v-else
          class="group/name flex-1 min-w-0 my-0 text-sm font-medium truncate text-n-slate-12 cursor-pointer hover:text-n-slate-12/80"
          :title="$t('CONTACT_PANEL.CLICK_TO_EDIT')"
          @click="startEditingName"
        >
          {{ contact.name }}
        </h3>
        <span
          v-if="contact.created_at"
          v-tooltip.left="
            `${$t('CONTACT_PANEL.CREATED_AT_LABEL')} ${dynamicTime(
              contact.created_at
            )}`
          "
          class="i-lucide-info text-sm text-n-slate-10 shrink-0"
        />
        <a
          :href="contactProfileLink"
          target="_blank"
          rel="noopener nofollow noreferrer"
          class="leading-3 shrink-0"
        >
          <span class="i-lucide-external-link text-sm text-n-slate-10" />
        </a>
      </div>

      <p v-if="additionalAttributes.description" class="break-words mb-0.5">
        {{ additionalAttributes.description }}
      </p>

      <!-- Một hàng chảy: số điện thoại, giới tính, tuổi đứng chung; địa chỉ
           chiếm cả hàng; khách mới cũ và chăm sóc chia đôi. Bề rộng từng ô lấy
           từ BE_RONG nên đổi cách xếp không phải sửa vòng lặp. -->
      <div class="flex flex-wrap items-end w-full gap-2">
        <div class="flex items-end flex-1 min-w-[84px] gap-2">
          <!-- Bấm vào ô là danh sách số bắt được trong chat thả xuống ngay,
               không phải đi tìm một cái mũi tên. Vẫn gõ tay số bất kỳ. -->
          <div class="relative flex-1 min-w-0">
            <input
              ref="oSdt"
              v-model="sdtNhap"
              class="reset-base w-full min-w-0 !h-8 !m-0 !px-2.5 !py-0 !text-sm !leading-8 !bg-n-alpha-1 !border-0 !rounded-lg !outline-0 text-n-slate-12 placeholder:text-n-slate-10 hover:!bg-n-alpha-2 focus-visible:!bg-n-alpha-2 focus-visible:!ring-1 focus-visible:!ring-n-brand"
              type="tel"
              :placeholder="$t('BONBOZ.CONTACT.PHONE')"
              @focus="moGoiY = true"
              @blur="moGoiY = false"
              @keydown.esc="moGoiY = false"
              @keydown.enter="luuSdt(sdtNhap)"
            />
            <!-- Rộng vừa đủ chứa số: bảng thả xuống rộng bằng cả cột chỉ để
                 chứa mười một chữ số trông rỗng loãng. -->
            <div
              v-if="moGoiY && sdtGoiY.length"
              class="absolute z-30 top-full left-0 mt-1 py-1 w-max max-w-full rounded-lg border border-n-weak bg-n-solid-2 shadow-lg"
            >
              <button
                v-for="sdt in sdtGoiY"
                :key="sdt"
                type="button"
                class="reset-base block w-full h-8 px-2.5 text-sm leading-8 text-left whitespace-nowrap text-n-slate-12 hover:bg-n-alpha-2"
                :title="$t('BONBOZ.CONTACT.PHONE_SUGGEST_APPLY', { sdt })"
                @mousedown.prevent="chonGoiY(sdt)"
              >
                {{ sdt }}
              </button>
            </div>
          </div>
          <!-- Nút Lưu chỉ hiện khi số đã bị sửa. Chỗ nó nhường lúc nằm im
               chính là chỗ cho ô giới tính và ô tuổi đứng cùng hàng.
               `mousedown` chứ không phải `click`: ô số mất focus trước khi
               click kịp bắn ra, nút biến mất ngay dưới ngón tay. -->
          <button
            v-if="sdtDaDoi"
            type="button"
            class="reset-base h-8 px-2.5 text-sm leading-8 rounded-lg shrink-0 bg-n-alpha-2 hover:bg-n-alpha-3 text-n-slate-12"
            :title="$t('BONBOZ.CONTACT.PHONE_SAVE')"
            @mousedown.prevent="luuSdt(sdtNhap)"
          >
            {{ $t('BONBOZ.CONTACT.PHONE_SAVE') }}
          </button>
        </div>

        <div
          v-for="truong in truongNhap"
          :key="truong.khoa"
          class="min-w-0"
          :class="BE_RONG[truong.khoa]"
        >
          <select
            v-if="truong.chon"
            :value="giaTri(contact, truong.khoa)"
            class="w-full !h-8 !m-0 !py-0 !pl-2.5 !pr-6 !text-sm !leading-8 !bg-n-alpha-1 !border-0 !rounded-lg !outline-0 cursor-pointer text-n-slate-12 hover:!bg-n-alpha-2 focus-visible:ring-1 focus-visible:ring-n-brand"
            @change="luuTruong(truong, $event.target.value)"
          >
            <option v-for="muc in mucChon(truong)" :key="muc" :value="muc">
              {{ muc || $t(`BONBOZ.HO_SO.${truong.nhan}`) }}
            </option>
          </select>
          <input
            v-else
            :value="giaTri(contact, truong.khoa)"
            class="reset-base w-full min-w-0 !h-8 !m-0 !px-2.5 !py-0 !text-sm !leading-8 !bg-n-alpha-1 !border-0 !rounded-lg !outline-0 text-n-slate-12 placeholder:text-n-slate-10 hover:!bg-n-alpha-2 focus-visible:!bg-n-alpha-2 focus-visible:!ring-1 focus-visible:!ring-n-brand"
            type="text"
            :inputmode="truong.so ? 'numeric' : 'text'"
            :placeholder="$t(`BONBOZ.HO_SO.${truong.nhan}`)"
            @keydown.enter="$event.target.blur()"
            @change="luuTruong(truong, $event.target.value)"
          />
        </div>
      </div>

      <SocialIcons :social-profiles="socialProfiles" />

      <!-- Đã bỏ các nút nhắn tin mới, sửa, gộp, xoá danh bạ: người trực không
           làm việc đó giữa lúc trả lời khách, sửa tên hay số thì bấm thẳng vào
           ô ở trên. Giữ nút gọi điện, nó tự ẩn khi chưa bật tổng đài. -->
      <div class="flex items-center w-full gap-2">
        <VoiceCallButton
          :phone="contact.phone_number"
          :contact-id="contact.id"
          :conversation-id="currentChat?.id"
          icon="i-lucide-phone"
          sm
          faded
          slate
          :tooltip-label="$t('CONTACT_PANEL.CALL')"
        />
      </div>
    </div>
  </div>
</template>
